/* ============================================================
   PRODUCT GALLERY CONTROLLER
   - Syncs scroll position <-> active slide <-> thumbnail state <-> counter
   - Listens for variant changes (via the existing data-product-single-media-id attr)
   - No external libraries
   ============================================================ */

(function () {
  'use strict';

  function ProductGallery(root) {
    this.root        = root;
    this.viewport    = root.querySelector('[data-gallery-viewport]');
    this.track       = root.querySelector('[data-gallery-track]');
    this.slides      = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-slide]'));
    this.prevBtn     = root.querySelector('[data-gallery-prev]');
    this.nextBtn     = root.querySelector('[data-gallery-next]');
    this.currentEl   = root.querySelector('[data-gallery-current]');
    this.totalEl     = root.querySelector('[data-gallery-total]');

    // Thumbnails sit outside the gallery root, but inside the wrap
    var wrap = root.closest('.product-gallery-wrap') || root.parentElement;
    this.thumbs = Array.prototype.slice.call(wrap.querySelectorAll('[data-gallery-thumb]'));

    this.currentIndex = this._findInitialIndex();
    this.scrollTimer  = null;

    this._bind();
    this._update(this.currentIndex, false);
  }

  ProductGallery.prototype._findInitialIndex = function () {
    for (var i = 0; i < this.slides.length; i++) {
      if (this.slides[i].classList.contains('is-active')) return i;
    }
    return 0;
  };

  ProductGallery.prototype._bind = function () {
    var self = this;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.go(self.currentIndex - 1);
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.go(self.currentIndex + 1);
      });
    }

    this.thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function (e) {
        e.preventDefault();
        self.go(i);
      });
    });

    // Keep state in sync when the user swipes / drags the track
    this.track.addEventListener('scroll', function () {
      if (self.scrollTimer) window.cancelAnimationFrame(self.scrollTimer);
      self.scrollTimer = window.requestAnimationFrame(function () {
        self._syncFromScroll();
      });
    }, { passive: true });

    // Keyboard navigation when gallery is focused
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); self.go(self.currentIndex - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); self.go(self.currentIndex + 1); }
    });

    // Hook into Shopify's variant option selectors so changing Color (or any
    // option) scrolls the gallery to that variant's featured image.
    this._bindVariantSync();
  };

  ProductGallery.prototype._bindVariantSync = function () {
    var self = this;

    // Find the actual section container (not the gallery itself).
    // The theme dispatches variantChange / variantImageChange on the section,
    // and they bubble up. We listen on the section if we can find it, but
    // also fall back to document-level so we catch the bubbling event no
    // matter how the markup is structured.
    var section = this.root.parentElement
      ? this.root.parentElement.closest('[data-section-id]')
      : null;

    function handleVariant(evt) {
      var variant = evt.detail && evt.detail.variant;
      if (!variant) return;
      var mediaId = (variant.featured_media && variant.featured_media.id) ||
                    (variant.featured_image && variant.featured_image.id);
      if (mediaId) self.goToMediaId(mediaId);
    }

    var target = section || document;
    target.addEventListener('variantChange', handleVariant);
    target.addEventListener('variantImageChange', handleVariant);
  };

  ProductGallery.prototype._syncFromScroll = function () {
    if (!this.slides.length) return;
    var trackRect = this.track.getBoundingClientRect();
    var midpoint  = trackRect.left + trackRect.width / 2;
    var closest   = 0;
    var minDist   = Infinity;

    for (var i = 0; i < this.slides.length; i++) {
      var rect = this.slides[i].getBoundingClientRect();
      var center = rect.left + rect.width / 2;
      var dist = Math.abs(center - midpoint);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    }

    if (closest !== this.currentIndex) {
      this._update(closest, false);
    }
  };

  ProductGallery.prototype.go = function (index) {
    if (index < 0) index = 0;
    if (index >= this.slides.length) index = this.slides.length - 1;
    if (index === this.currentIndex) return;
    this._update(index, true);
  };

  ProductGallery.prototype.goToMediaId = function (mediaId) {
    if (!mediaId) return;
    var idStr = String(mediaId);
    // Match either the media id or the underlying image id — variants on
    // older products only carry `featured_image` (image id), while modern
    // ones carry `featured_media` (media id). The two numbers are different
    // for the same picture, so we look up under both attributes.
    for (var i = 0; i < this.slides.length; i++) {
      var slide = this.slides[i];
      if (slide.getAttribute('data-gallery-media-id') === idStr ||
          slide.getAttribute('data-gallery-image-id') === idStr) {
        this.go(i);
        return;
      }
    }
  };

  ProductGallery.prototype._update = function (index, scroll) {
    this.currentIndex = index;

    // Scroll to the slide.
    // Use index * slideWidth instead of slide.offsetLeft — offsetLeft
    // returns position relative to the nearest positioned ancestor, which
    // isn't necessarily the track, so it can give wildly wrong values.
    if (scroll && this.slides[index]) {
      var slideWidth = this.slides[index].getBoundingClientRect().width;
      this.track.scrollTo({
        left: index * slideWidth,
        behavior: 'smooth'
      });
    }

    // Update slide active class
    this.slides.forEach(function (s, i) {
      s.classList.toggle('is-active', i === index);
    });

    // Update thumbnails
    this.thumbs.forEach(function (t, i) {
      var on = i === index;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    // Update counter
    if (this.currentEl) this.currentEl.textContent = index + 1;

    // Update arrow disabled state
    if (this.prevBtn) {
      if (index === 0) this.prevBtn.setAttribute('disabled', '');
      else this.prevBtn.removeAttribute('disabled');
    }
    if (this.nextBtn) {
      if (index === this.slides.length - 1) this.nextBtn.setAttribute('disabled', '');
      else this.nextBtn.removeAttribute('disabled');
    }
  };

  function init() {
    document.querySelectorAll('[data-product-gallery]').forEach(function (el) {
      if (el.__galleryInited) return;
      el.__galleryInited = true;
      new ProductGallery(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init if Shopify section is re-rendered in the theme editor
  document.addEventListener('shopify:section:load', init);
})();